// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { FileManagerInstance } from "filemanager/FileManager";
import { Dispatch } from 'redux';
import { frontendSlice } from "store/frontend/frontendSlice";
import { AppDispatch, AppState } from '..';
import { ClusterApi, ClusterDocument, ClusterInfo, DocumentApi, DocumentStatus } from '../../networking/api';
import { reportErrorEncountered } from "../authentication/actions";
import { appendDocuments, updateDocument } from '../documents/actions';
import { DocumentState, InitialDocState } from "../documents/types";
import { ADD_NEW_CLUSTER, ADD_TO_CLUSTER, ClustersActionTypes, ClustersState, ClusterState, FetchStateUpdate, REMOVE_CLUSTERS, REMOVE_FROM_CLUSTER, SET_DOC_FETCH_STATE, UPDATE_CLUSTER_INFO, UPDATE_ClUSTERS_STATE } from "./types";

export function updateClustersData(newState: ClustersState): ClustersActionTypes {
    return {
        type: UPDATE_ClUSTERS_STATE,
        payload: newState
    };
}

export function setDocumentsFetchState(fetchState: FetchStateUpdate): ClustersActionTypes {
    return {
        type: SET_DOC_FETCH_STATE,
        payload: fetchState
    }
}

export function _updateClusterInfo(clusterInfo: ClusterInfo): ClustersActionTypes {
    return {
        type: UPDATE_CLUSTER_INFO,
        payload: clusterInfo
    }
}

export function removeFromCluster(clusterId: string, folderIds: string[]): ClustersActionTypes {
    return {
        type: REMOVE_FROM_CLUSTER,
        payload: { clusterId: clusterId, folderIds: folderIds }
    }
}

export function addToCluster(clusterId: string, folderIds: string[]): ClustersActionTypes {
    return {
        type: ADD_TO_CLUSTER,
        payload: { clusterId: clusterId, folderIds: folderIds }
    }
}

export function addNewCluster(clusterInfo: ClusterInfo): ClustersActionTypes {
    return {
        type: ADD_NEW_CLUSTER,
        payload: clusterInfo
    }
}

export function removeClusters(clusterIds: string[]): ClustersActionTypes {
    return {
        type: REMOVE_CLUSTERS,
        payload: clusterIds
    }
}

export function fetchClusters(force: boolean = false) {
    return async function (dispatch: AppDispatch, getState: (() => AppState)) {
        if (!force && (getState().clusters.isFetching || getState().clusters.fetched)) {
            return Promise.resolve();
        }

        dispatch(updateClustersData({ isFetching: true, fetched: false, indexed: {}, allIds: [] }))

        const clustersApi = new ClusterApi();
        try {
            const response = await clustersApi.clusterGetClusters();
            const clusters = response.data;
            const ids: string[] = [];
            const dict: {[clusterId: string]: ClusterState} = {};

            for (let clusterInfo of clusters) {
                ids.push(clusterInfo.clusterId!);
                dict[clusterInfo.clusterId!] = {
                    documentsFetchState: {
                        fetching: false,
                        fetched: false
                    },
                    clusterInfo: clusterInfo,
                    docIds: []
                };
            }

            dispatch(updateClustersData({
                isFetching: false,
                fetched: true,
                indexed: dict,
                allIds: ids
            }));
        }
        catch (error) {
            dispatch(updateClustersData({ isFetching: false, fetched: false, indexed: {}, allIds: [] }))
            dispatch(reportErrorEncountered('fetchClusters', error as Error));
        }
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function _setCurClusterEnsureDocsGetChunkTypes(
    clusterId: string,
    dispatch: Dispatch<any>,
    getState: (() => AppState)): Promise<void | void[]> {
    while (getState().clusters.isFetching) {
        await sleep(100);
    }

    if (!(clusterId in getState().clusters.indexed)) {
        throw Error(`${clusterId} not found in fetched clusters`);
    }

    var promise = null;

    var fetchState = getState().clusters.indexed[clusterId].documentsFetchState;
    // Only fetch document state if it has never been fetched before
    if (!fetchState.fetched && !fetchState.fetching) {
        promise = updateDocsForCluster(clusterId, dispatch, getState);
    } else {
        promise = Promise.resolve();
    }

    return promise;
}

function updateDocsForCluster(
    clusterId: string,
    dispatch: Dispatch<any>,
    getState: (() => AppState)): Promise<void | void[]> {
    if (!(clusterId in getState().clusters.indexed)) {
        throw Error(`${clusterId} not found in fetched clusters`);
    }

    let fetchState = getState().clusters.indexed[clusterId].documentsFetchState;
    if (!fetchState.fetching) {
        dispatch(setDocumentsFetchState({ clusterId: clusterId, fetchState: { fetching: true, fetched: false } }));

        let documentApi = new DocumentApi();
        return documentApi.documentGetDocuments(clusterId)
            .then((axios_response) => {

                let docs = axios_response.data;
                let newDocumentFolderIds: string[] = [];
                let allFolderIds: string[] = [];
                let dict: { [id: string]: DocumentState } = {};

                let state = getState();
                for (let documentInfo of docs) {
                    if (!!!documentInfo.folderId) {
                        continue;
                    }

                    allFolderIds.push(documentInfo.folderId);
                    if (!(state.documents.indexed[documentInfo.folderId])) {
                        newDocumentFolderIds.push(documentInfo.folderId);
                        dict[documentInfo.folderId] = InitialDocState(documentInfo);
                    }
                }

                // Update our document store by appending any newly discovered docs
                dispatch(appendDocuments({ indexed: dict, allIds: newDocumentFolderIds }));

                // Add all of the documents to the given cluster
                dispatch(addToCluster(clusterId, allFolderIds));

                // Mark this fetch operation as complete
                dispatch(setDocumentsFetchState({ clusterId: clusterId, fetchState: { fetching: false, fetched: true } }));
            })
            .catch((error) => {
                dispatch(reportErrorEncountered('updateDocsForCluster', error));
            });
    }
    else {
        return Promise.resolve();
    }
}

export function setCurClusterAndEnsureDocumentsAndChunkTypes(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clusterId: string): ((dispatch: Dispatch<any>, getState: (() => AppState)) => Promise<any>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        // @ts-ignore
        return _setCurClusterEnsureDocsGetChunkTypes(clusterId, dispatch, getState);
    }
}


export function renameCluster(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clusterId: string,
    label: string): ((dispatch: Dispatch<any>, getState: (() => AppState)) => Promise<any>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        if (!clusterId || !label) {
            return;
        }

        const state = getState();
        const cluster = state.clusters.indexed[clusterId];

        if (!!cluster) {
            if (cluster.clusterInfo.label === label) {
                return;
            }

            const info = {
                ...cluster.clusterInfo,
                label: label
            };

            dispatch(_updateClusterInfo(info));
        }

        const clusterApi = new ClusterApi();
        await clusterApi.clusterRenameCluster(clusterId, label);
    }
}

export function handleRefetchCluster(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clusterId: string): ((dispatch: Dispatch<any>, getState: (() => AppState)) => void) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        const clustersApi = new ClusterApi();
        try {
            const response = await clustersApi.clusterGetCluster(clusterId);
            dispatch(_updateClusterInfo(response.data));
            dispatch(tryRefreshDocSetDocuments(clusterId));
        } catch (error) {
            dispatch(reportErrorEncountered('fetchCluster', error as Error));
        }
    }
}

export function handleClusterCreated(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clusterInfo: ClusterInfo): ((dispatch: Dispatch<any>, getState: (() => AppState)) => void) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        const state = getState();
        const existingCluster = state.clusters.indexed[clusterInfo.clusterId!];
        if (!existingCluster) {
            // The client does not yet know about this cluster, so let's add it
            dispatch(addNewCluster(clusterInfo));
        }
    }
}

export function changeClusterForDocuments(
    docSetId: string, paths: string[]): ((dispatch: Dispatch<any>, getState: (() => AppState)) => Promise<any>) {
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)): Promise<any> {
        if (paths.length === 0) {
            return;
        }

        const assignmentRequest = !!docSetId ? FileManagerInstance.assignFiles(docSetId, paths) : FileManagerInstance.unassignFiles(paths);
        await assignmentRequest;

        if (!!docSetId) {
            dispatch(tryRefreshDocSetDocuments(docSetId));
        }
    }
}

export function tryRefreshDocSetDocuments(
    docSetId: string): ((dispatch: Dispatch<any>, getState: (() => AppState)) => Promise<any>) {
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)): Promise<any> {
        const state = getState();
        if (!!state.clusters.indexed[docSetId]?.documentsFetchState.fetched) {
            return updateDocsForCluster(docSetId, dispatch, getState);
        }

        dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-documents', id: docSetId }]));
        return Promise.resolve();
    }
}

export function ensureDocSetDocuments(
    docSetId: string): ((dispatch: AppDispatch, getState: (() => AppState)) => Promise<any>) {
    return async function (dispatch: AppDispatch, getState: (() => AppState)): Promise<any> {
        const state = getState();
        if (!state.clusters.indexed[docSetId]?.documentsFetchState.fetched) {
            updateDocsForCluster(docSetId, dispatch, getState);
        }
    }
}

export function handleDocumentUpdate(document: ClusterDocument) {
    return function (dispatch: AppDispatch, getState: (() => AppState)) {
        const state = getState();
        const existingDocumentId = state.documents.allIds.find(docId => createFolderId(docId).fileId === document.metadataId) ?? '';
        const existingDocument = state.documents.indexed[existingDocumentId]?.clusterDocument;
        if (!!existingDocument) {
            const { clusterId: existingClusterId } = createFolderId(existingDocumentId);
            if (existingClusterId !== document.clusterId) {
                // The docset has changed, we need to update the docset state as well
                dispatch(removeFromCluster(existingClusterId, [existingDocumentId]));
                dispatch(invalidateDocsetStatusFromDocumentUpdate(existingClusterId, existingDocument));
            }
        }

        // Invalidate the storage providers if the document comes from a provider that is not in the list
        if (!!document.remoteStorageMetadata?.providerId) {
            const providers = frontendSlice.endpoints.getStorageProviders.select()(state);
            const provider = providers?.data?.providers.some(id => id === document.remoteStorageMetadata!.providerId);
            if (!provider) {
                dispatch(frontendSlice.util.invalidateTags(['storage-providers']));
            }
        }

        dispatch(updateDocument({document: document}));
    }
}

export function invalidateDocsetStatusFromDocumentUpdate(docSetId: string, document: ClusterDocument) {
    return function (dispatch: AppDispatch, getState: (() => AppState)) {
        // Only handle the docset status invalidation if the document is ready
        if (document.status !== DocumentStatus.Ready) {
            return;
        }
        dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-status', id: docSetId }]));
    }
}
